<template>
  <v-container>
    <v-data-table
      :items="consumerLoans"
      :headers="headers"
      :loading="indexLoading"
      dense
      :page.sync="page"
      :items-per-page.sync="itemsPerPage"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :server-items-length="totalItems"
      show-expand
      item-key="id"
      single-expand
    >
      <!-- SEARCH -->
      <template v-slot:top>
        <div class="px-2 py-1">
          <v-text-field
            v-model="search"
            label="Search"
            clearable
            hide-details
            append-icon="mdi-magnify"
          />
        </div>
      </template>
      <template v-slot:expanded-item="{ item, headers }">
        <td :colspan="headers.length" class="pa-0">
          <v-card flat class="pa-1">
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Item</th>
                  <th class="text-right">
                    Qty
                  </th>
                  <th class="text-right">
                    Price
                  </th>
                  <th class="text-right">
                    Disc
                  </th>
                  <th class="text-right">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(i, idx) in item.items" :key="idx">
                  <td>
                    <span
                      v-html="highlightKeyword(i?.stock?.product_name, search)"
                    />
                    ({{ i?.stock?.stock_code }})
                  </td>
                  <td class="text-right">
                    {{ i.quantity }}
                  </td>
                  <td class="text-right">
                    {{ formatMoney(i.retail) }}
                  </td>
                  <td class="text-right">
                    {{ formatMoney(i.discount) }}
                  </td>
                  <td class="text-right">
                    {{ formatMoney(i.amount) }}
                  </td>
                </tr>
                <tr v-if="!item.items || item.items.length === 0">
                  <td colspan="5" class="text-center grey--text">
                    No Items Found
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </td>
      </template>
      <!-- RECEIPT STATUS -->
      <template v-slot:item.receipt_status="{ item }">
        <v-chip small :color="item.receipt_returned ? 'green' : 'grey'" dark>
          {{ item.receipt_returned ? 'Returned' : 'Not Returned' }}
        </v-chip>
      </template>
      <template v-slot:item.id="{ value }">
        <span v-html="highlightKeyword(value, search)" />
      </template>
      <template v-slot:item.loanee_name="{ value }">
        <span v-html="highlightKeyword(value, search)" />
      </template>
      <!-- CONFIG -->
      <template v-slot:item.config="{ item, expand, isExpanded }">
        <div class="config">
          <!-- DELETE -->
          <v-icon
            v-if="authPermissions.has('consumer_loans:delete')"
            color="error"
            @click="destroy(item.id)"
          >
            mdi-delete
          </v-icon>

          <!-- EDIT -->
          <v-icon
            v-if="authPermissions.has('consumer_loans:edit')"
            color="yellow"
            @click="editLoan(item)"
          >
            mdi-pencil
          </v-icon>

          <!-- INVENTORIES -->
          <v-icon color="green" @click="toConsumerLoanInventories(item)">
            mdi-clipboard-multiple
          </v-icon>

          <!-- TRANSACTIONS -->
          <v-icon color="green" @click="toConsumerLoanTransactions(item)">
            mdi-account-cash
          </v-icon>

          <!-- PRINT -->
          <v-progress-circular v-if="printingPDF?.[item.id]" size="20" />
          <v-icon v-else color="blue" @click="printReceipt(item)">
            mdi-file
          </v-icon>

          <!-- 🔥 RETURN / UNRETURN RECEIPT -->
          <v-icon
            :color="item.receipt_returned ? 'orange' : 'green'"
            :title="
              item.receipt_returned ? 'Unreturn Receipt' : 'Return Receipt'
            "
            @click="toggleReceipt(item)"
          >
            {{
              item.receipt_returned ? 'mdi-backup-restore' : 'mdi-check-circle'
            }}
          </v-icon>
        </div>
      </template>

      <!-- AMOUNT -->
      <template v-slot:item.total_amount="{ item }">
        {{ formatMoney(item.total_amount) }}
      </template>

      <!-- BALANCE -->
      <template v-slot:item.balance="{ item }">
        <span :class="remarksColor(item.remark)">
          {{ formatMoney(item.balance) }}
        </span>
      </template>

      <!-- PROGRESS -->
      <template v-slot:item.progress="{ item }">
        <span :class="`${statusColor(item)}--text`">
          <div v-if="item.remark === 'No Items Found!'">No Items</div>
          <div v-else-if="item.debt === 0">Fully Paid</div>
          <div v-else>
            {{
              (
                100 * divide(item.total_transaction, item.total_amount || 1)
              ).toFixed(1)
            }}%
          </div>
        </span>
      </template>

      <!-- REMARK -->
      <template v-slot:item.remark="{ item }">
        <strong :class="remarksColor(item.remark)">
          {{ item.remark }}
        </strong>
      </template>

      <!-- DATES -->
      <template v-slot:item.date_loaned="{ item }">
        {{ getCurrentDate(new Date(item.date_loaned), 'en-US') }}
      </template>

      <template v-slot:item.date_due="{ item }">
        {{ getCurrentDate(new Date(item.date_due), 'en-US') }}
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import {
  getIndex,
  updateAction,
} from '@/repositories/v2/consumer-loans.repository'
import consumerLoanRepository from '@/repositories/consumer-loan.repository'
import { moneyMixin } from '@/views/dashboard/mixins/helper.mixins'
import _ from 'lodash'
import { mapState } from 'vuex'
import { AxiosInternalServerError } from '@/errors/AxiosInternalServerError'
import {
  ErrorPopup,
  SuccessPopup,
  LoadingPopup,
  getCurrentDate,
  formatAccounting,
  highlightKeyword,
} from '@/lib/utils'

import Receipt from '@/pdf/receipt.pdf'

export default {
  mixins: [moneyMixin],

  data() {
    return {
      totalItems: 10,
      consumerLoans: [],
      indexLoading: false,
      search: null,
      headers: [
        { text: 'Config', value: 'config', sortable: false },
        { text: 'CR #', value: 'id', align: 'right' },
        { text: 'Name', value: 'loanee_name' },
        { text: 'Loaned', value: 'date_loaned' },
        { text: 'Due', value: 'date_due' },
        { text: 'Amount', value: 'total_amount', align: 'right' },
        { text: 'Balance', value: 'balance', align: 'right' },
        { text: 'Progress', value: 'progress', align: 'right' },
        { text: 'Remark', value: 'remark' },
        { text: 'Receipt', value: 'receipt_status' },
      ],
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: [],
    }
  },

  computed: {
    ...mapState({
      authPermissions: (state) => state.auth.userPermissions,
    }),
  },

  watch: {
    search: _.debounce(function () {
      this.getConsumerLoans()
    }, 500),
    page: _.debounce(function () {
      this.getConsumerLoans()
    }, 500),
    itemsPerPage: _.debounce(function () {
      this.getConsumerLoans()
    }, 500),
    sortBy: {
      deep: true,
      handler: _.debounce(function () {
        this.onSortChanged()
      }, 500),
    },
    sortDesc: {
      deep: true,
      handler: _.debounce(function () {
        this.onSortChanged()
      }, 500),
    },
  },

  created() {
    this.initSearchParams()
    this.getConsumerLoans()
  },

  methods: {
    getSearchParams() {
      const params = _.omitBy(
        {
          search: this.search,
          page: this.page,
          itemsPerPage: this.itemsPerPage,
          sortBy: this.sortBy?.[0],
          sortDesc: this.sortDesc?.[0] ? 1 : undefined,
        },
        _.isNil,
      )
      return new URLSearchParams(params)
    },
    initSearchParams() {
      if (this.$route.query?.search) this.search = this.$route.query?.search
      if (this.$route.query?.page) this.page = this.$route.query?.page
      if (this.$route.query?.itemsPerPage)
        this.itemsPerPage = this.$route.query?.itemsPerPage
      if (this.$route.query?.sortBy) this.sortBy = [this.$route.query.sortBy]
      if (this.$route.query?.sortDesc) this.sortDesc = [true]
    },
    // Sorting restarts the listing; the page watcher does the fetch when the
    // page actually moves, so only fetch here when it is already on page 1
    onSortChanged() {
      if (Number(this.page) !== 1) {
        this.page = 1
        return
      }
      this.getConsumerLoans()
    },
    // 🔄 FETCH LIST
    async getConsumerLoans() {
      try {
        this.indexLoading = true
        const { data } = await getIndex({
          storeId: this.storeId,
          search: this.search,
          consumer_id: this.$route.params.consumer,
          page: this.page,
          limit: this.itemsPerPage,
          sort_by: this.sortBy?.[0] || undefined,
          sort_desc: this.sortDesc?.[0] ? 1 : 0,
        })
        this.consumerLoans = data.data
        this.totalItems = data.meta.total
      } catch (e) {
        if (e instanceof AxiosInternalServerError) {
          ErrorPopup.fire('Error', e.message)
          return
        }
        if (e instanceof Error)
          ErrorPopup.fire('Error', 'an unexpected error has occured')
      } finally {
        this.indexLoading = false
      }
    },

    // 🗑 DELETE
    async destroy(id) {
      try {
        const confirm = await this.$confirm(
          'Are you sure you want to delete this record?',
        )
        if (!confirm) return
        await consumerLoanRepository.delete(id)
        this.getConsumerLoans()
      } catch (error) {
        if (error instanceof Error) {
          console.error(error.message)
        }
        ErrorPopup.fire()
      }
    },

    // ✏️ EDIT
    editLoan(item) {
      this.$emit('edit', item)
    },

    // 🧾 PRINT RECEIPT
    async printReceipt(item) {
      try {
        LoadingPopup.fire('Loading', 'Generating PDF...')
        const receiptNo = item.id
        const items = item.items.map((i) => {
          return {
            item: `${i.stock.product_name} (${i?.stock?.stock_code})`,
            quantity: i.quantity,
            price: formatAccounting(i.retail),
            discount: formatAccounting(i.discount),
            amount: formatAccounting(i.amount),
          }
        })

        const receipt = new Receipt()

        receipt.generate(
          {
            totalAmount: item.total_amount,
            amountPaid: item.total_transaction,
            balance: item.balance,
            receiptNo,
            name: item.consumer.name,
            dateLoaned: item.date_loaned
              ? getCurrentDate(new Date(item.date_loaned), 'en-US')
              : '',
          },
          items,
        )
        LoadingPopup.close()
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error(e)
        }
        if (e instanceof Error) ErrorPopup.fire()
      }
    },

    /**
     * Description
     * @param {string} remarks
     */
    remarksColor(remarks) {
      if (remarks === 'Overdue!') return 'red--text'
      if (remarks === 'Due Today!') return 'yellow--text'
      if (remarks === 'Fully Paid (Late Payment)!') return 'orange--text'
      if (remarks === 'Fully Paid!') return 'green--text'
      if (remarks === 'Not Yet Due!') return ''
      if (remarks === 'No Items Found!') return ''
      throw new Error('Invalid argument [remarks] for remarksColor')
    },
    // 🔥 RETURN / UNRETURN RECEIPT
    async toggleReceipt(item) {
      try {
        const isConfirmed = await confirm(
          !item.receipt_returned
            ? 'are you sure the receipt has been returned?'
            : 'are you sure you want to unde the returned receipt?',
        )
        if (!isConfirmed) return
        LoadingPopup.fire(
          'Loading',
          item.receipt_returned ? 'Undoing return' : 'returning receipt',
        )
        const action = item.receipt_returned
          ? 'unreturn-receipt'
          : 'return-receipt'

        const { data } = await updateAction(item.id, action)

        SuccessPopup.fire('Success', data)
        // 🔁 Refresh list
        this.getConsumerLoans()
      } catch (e) {
        LoadingPopup.close()
        if (e instanceof Error) ErrorPopup.fire()
      }
    },

    statusColor(debt, paid) {
      if (debt === 0) return 'primary'
      const percentage = 100 * (paid / debt)
      if (percentage < 20) return 'red'
      if (percentage < 40) return 'orange'
      if (percentage < 70) return 'yellow'
      return 'green'
    },

    // 📋 INVENTORIES
    toConsumerLoanInventories({ id }) {
      this.$router.push({
        name: 'Consumer.Loan.Inventories',
        params: {
          consumer: this.$route.params.consumer,
          consumerLoan: id,
        },
        query: {
          callback: this.$route.path + '?' + this.getSearchParams(),
        },
      })
    },

    // 💰 TRANSACTIONS
    toConsumerLoanTransactions({ id }) {
      this.$router.push({
        name: 'Consumer.Loan.Transactions',
        params: {
          consumer: this.$route.params.consumer,
          consumerLoan: id,
        },
        query: {
          callback: this.$route.path + '?' + this.getSearchParams(), // encode as URL-safe string
        },
      })
    },
    divide: _.divide,
    highlightKeyword,
    getCurrentDate,
  },
}
</script>

<style scoped>
.config {
  display: flex;
}
.config .v-icon {
  margin-right: 6px;
}
</style>
